import { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { CONTACT } from "../data";
import { useLang } from "../LanguageContext";
import { LanguageSwitcher } from "./LanguageSwitcher";

export const Header = ({ onCtaClick }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const links = [
    { label: t.nav.quiz, href: "#quiz" },
    { label: t.nav.offer, href: "#oferta" },
    { label: t.nav.fleet, href: "#flota" },
    { label: t.nav.about, href: "#o-nas" },
    { label: t.nav.how, href: "#jak-to-dziala" },
    { label: t.nav.faq, href: "#faq" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <a href="#top" onClick={(e) => { e.preventDefault(); go("#top"); }} data-testid="logo-link" className="flex items-center gap-2.5 group">
          <img
            src={CONTACT.logo}
            alt=""
            data-testid="logo-image"
            className="w-10 h-10 object-contain bg-white rounded-sm p-0.5"
            onError={(event) => { event.currentTarget.style.display = "none"; }}
          />
          <span className="flex flex-col leading-none">
            <span className="font-display font-bold text-white text-lg tracking-tight">DRON</span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#A3A3A3]">{t.header.descriptor}</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.href.replace("#", "")}`}
              onClick={() => go(l.href)}
              className="text-sm text-[#A3A3A3] hover:text-white transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSwitcher className="hidden sm:flex" />
          <a
            href={CONTACT.phoneHref}
            data-testid="header-phone"
            className="hidden xl:flex items-center gap-2 text-sm text-white/90 hover:text-white transition-colors"
          >
            <Phone size={16} className="text-[#E02020]" />
            {CONTACT.phone}
          </a>
          <button
            data-testid="header-cta"
            onClick={onCtaClick}
            className="hidden sm:inline-flex bg-[#E02020] hover:bg-[#B91818] text-white text-sm font-medium px-5 py-2.5 rounded-sm transition-colors"
          >
            {t.header.cta}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 px-5 py-6 flex flex-col gap-4">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`mobile-nav-${l.href.replace("#", "")}`}
              onClick={() => go(l.href)}
              className="text-left text-base text-white/90 py-1"
            >
              {l.label}
            </button>
          ))}
          <a href={CONTACT.phoneHref} className="flex items-center gap-2 text-white/90 py-1">
            <Phone size={18} className="text-[#E02020]" /> {CONTACT.phone}
          </a>
          <LanguageSwitcher className="py-1" />
          <button
            data-testid="mobile-cta"
            onClick={() => { setOpen(false); onCtaClick(); }}
            className="bg-[#E02020] text-white font-medium px-5 py-3 rounded-sm mt-2"
          >
            {t.header.ctaFull}
          </button>
        </div>
      )}
    </header>
  );
};
